import { useAppDispatch, useAppSelector } from "app/store/store";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import MemoHeartOutline from "../icons/HeartOutline";
import MemoPlay from "../icons/Play";
import styles from "./podcast-player-fullscreen.module.scss";
import { Category, PodcastChapter } from "services-hooks/types";
import MemoPause from "../icons/Pause";
import useModalScroll from "app/hooks/modal-scroll.hook";
import MemoAngleDown from "../icons/AngleDown";
import MemoInfo from "../icons/Info";
import MemoLoader from "../icons/Loader";
import clsx from "clsx";
import MemoWindForward from "../icons/WindForward";
import MemoWindBack from "../icons/WindBack";
import { ProgressSlider } from "../progress-slider/progress-slider.component";
import { useFavorites } from "app/hooks/favorites.hook";
import Loader from "../icons/Loader";
import MemoHeartFull from "../icons/HeartFull";
import { useArrowKeyNavigationOverlay } from "app/hooks/keyboard-navigation.hook";
import { useGoBackEvent } from "app/hooks/go-back.hook";
import { CSSTransition } from "react-transition-group";
import { playerActions, selectPlayerState } from "app/store/slices/player";
import { formatTimeWithUnits } from "app/utils/date";
import {
  useClickAway,
  useInterval,
  useNetworkState,
  useWindowSize,
} from "react-use";
import MemoArrowBack from "../icons/ArrowBack";
import { useNavigatePath } from "app/hooks/navigation.hook";
import MemoSpeedControl from "../icons/SpeedControl";
import MemoBigPlus from "../icons/BigPlus";
import MemoBigMinus from "../icons/BigMinus";
import MemoPlaylist from "../icons/Playlist";
import { HTMLMediaControls, HTMLMediaState } from "app/hooks/audio.hook";
import { format, parseISO } from "date-fns";
import { getDateFnsLocale } from "app/utils/language";
import { useTranslate } from "app/hooks/lang";
import { useFeaturesContext } from "app/context/features.context";
import { selectUserState } from "app/store/slices/user";
import { VlybyContainer } from "../station-player/vlyby-container.component";
import Link from "next/link";
import { Button } from "../ui/button.component";
import { timeout } from "app/ts";
import { usePlayerContext, VLYBY_AD_ERROR_TIMEOUT, VLYBY_TIMEOUT } from "app/context/player.context";
import { useCountryContext } from "app/context/country.context";
import { useRouter } from "next/router";
import { useGoToPodcast } from "app/hooks/navigation.hook";

interface PlayerFullScreenProps {
  playerCssVariables: object;
  podcastChapter: PodcastChapter;
  podcast: Category;
  onClose: VoidFunction;
  playbackState: HTMLMediaState;
  playbackControls: HTMLMediaControls;
  open: boolean;
}

export const PlayerFullScreen: React.FC<PlayerFullScreenProps> = ({
  playerCssVariables,
  podcastChapter,
  podcast,
  onClose,
  playbackState,
  playbackControls,
  open,
}) => {
  const navigatePath = useNavigatePath();
  const { toggleFavorite, loading, isFavoriteCategory } = useFavorites(() => {
    onClose();
    navigatePath("/log-in", true, { openplayer: true });
  });
  const [currentTime, setCurrentTime] = useState(0);
  const [streamDragging, setStreamDragging] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [speedSidebarOpen, setSpeedSidebarOpen] = useState(false);
  const goToPodcast = useGoToPodcast();
  useInterval(() => {
    if (!streamDragging) {
      setCurrentTime(playbackState.time);
    }
  }, 1000);

  const speedStep = 0.25;

  const playbackSpeedUp = () => {
    if (playbackState.playbackRate < 2 && !playbackState.activeAdStream) {
      playbackControls.setPlaybackRate(playbackState.playbackRate + speedStep);
    }
  };

  const playbackSpeedDown = () => {
    if (
      playbackState.playbackRate > 2 * speedStep &&
      !playbackState.activeAdStream
    ) {
      playbackControls.setPlaybackRate(playbackState.playbackRate - speedStep);
    }
  };

  const goToPodcastDetails = () => {
    goToPodcast(`/${podcast.permalink}`);
    onClose();
  };

  useModalScroll(open);
  const goBackCallback = useCallback(() => {
    onClose();
  }, [onClose]);
  useGoBackEvent(
    "PodcastPlayerFullScreen",
    goBackCallback,
    open && !sidebarOpen && !speedSidebarOpen
  );

  const formatTime = (seconds: number | undefined) => {
    if (seconds !== undefined) {
      let secondsDiff = seconds;
      const hoursDiff = Math.floor(secondsDiff / 3600);
      secondsDiff -= hoursDiff * 3600;
      const minutesDiff = Math.floor(secondsDiff / 60);
      secondsDiff -= minutesDiff * 60;
      secondsDiff |= 0;

      return `${hoursDiff.toString().padStart(2, "0")}:${minutesDiff
        .toString()
        .padStart(2, "0")}:${secondsDiff.toString().padStart(2, "0")}`;
    }
    return "";
  };

  const handleChange = (values: any) => {
    if (!playbackState.activeAdStream) {
      playbackControls.seek(Number(values[0]));
      setStreamDragging(false);
    }
  };

  const playerRange = useMemo(
    () => ({ min: 0, max: playbackState.duration }),
    [playbackState.duration]
  );

  const wrapperRef = useArrowKeyNavigationOverlay(open);

  const jumpForward = () => {
    if (!playbackState.activeAdStream) {
      playbackControls.seek(playbackState.time + 10);
    }
  };
  const jumpBack = () => {
    if (!playbackState.activeAdStream) {
      playbackControls.seek(playbackState.time - 10);
    }
  };
  const sidebarCloseCallback = useCallback(
    () => setSidebarOpen(false),
    [setSidebarOpen]
  );
  const speedSidebarCloseCallback = useCallback(
    () => setSpeedSidebarOpen(false),
    [setSpeedSidebarOpen]
  );

  // VLYBY
  const { session } = useAppSelector(selectUserState);
  const t = useTranslate();
  const { premiumVersion, vlybyPublisherId } = useFeaturesContext();
  const { country } = useCountryContext();
  const [videoAdActive, setVideoAdActive] = useState(false);
  const { width, height } = useWindowSize();
  const controlsContainerRef = useRef<HTMLDivElement>(null);
  const { setGlobalAdCloseTime, globalAdCloseTime, checkGlobalTimer, priceData } =
    usePlayerContext();
  const networkState = useNetworkState();
  const enableVlyby = !!vlybyPublisherId && !premiumVersion;
  const [isVlybyAdReady, setIsVlybyAdReady] = useState(false);
  const [isFirstVlybyAdPlay, setIsFirstVlybyAdPlay] = useState(true);
  const getIsPageVisible = useCallback(() => {
    if (typeof window !== "undefined") {
      return window.document.visibilityState === "visible";
    }
    return true;
  }, [window]);
  const [isAdblockDetected, setIsAdblockDetected] = useState(false);
  const hideAdTimeoutId = useRef<any>(null);
  const showAdTimeoutId = useRef<any>(null);
  const adFreeTime = 3 * 60000; //3min
  const firstAudioAdTimeout = 30000; //30s

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        (window as any).innerHideVlybyPlayer = function () {

          stopVlybyVideoAds((window as any).isVlybyAdReady);
        };
        (window as any).innerVlybyAdReady = function () {

          (window as any).isVlybyAdReady = true;
          setIsVlybyAdReady(true);
        };
      }
    } catch (error) {}

    return () => {
      try {
        if (typeof window !== "undefined") {
          delete (window as any).innerHideVlybyPlayer;
          delete (window as any).innerVlybyAdReady;
          delete (window as any).isVlybyAdReady;
        }
        clearTimeout(hideAdTimeoutId.current);
        clearTimeout(showAdTimeoutId.current);
      } catch (error) {}
    };
  }, []);

  const stopVlybyVideoAds = async (
    isSuccess = false,
    stopNextTimeout = false,
    isAdError = false
  ) => {
    console.log("stopVlybyVideoAds");
    clearTimeout(hideAdTimeoutId.current);
    clearTimeout(showAdTimeoutId.current);

    (window as any).isVlybyAdReady = false;
    setIsVlybyAdReady(false);
    setVideoAdActive(false);
    setShowVlyby(false);

    if (isSuccess) {
      setGlobalAdCloseTime(Number(Date.now()));
    }
    if (enableVlyby && !stopNextTimeout) {
      showAdTimeoutId.current = setTimeout(() => {
        startLoadingVlyby();
      }, isAdError ? VLYBY_AD_ERROR_TIMEOUT : adFreeTime);
    }
  };

  // test adblock
  useEffect(() => {
    if (typeof window !== "undefined") {
      const adBoxEl = window.document.querySelector(".ad-box");
      setIsAdblockDetected(
        !!adBoxEl && window.getComputedStyle(adBoxEl)?.display === "none"
      );
    }
  }, []);

  const [showVlyby, setShowVlyby] = useState(false);

  const startLoadingVlyby = () => {
    if (!videoAdActive) {
      clearTimeout(hideAdTimeoutId.current);

      const localCheck =
        !!vlybyPublisherId &&
        country.countryCode !== "de" &&
        getIsPageVisible() &&
        !session?.user?.premium &&
        !isAdblockDetected;

      console.log("[[VLYBY]] START LOADING", localCheck, {
        open,
        isFirstVlybyAdPlay,
        globalAdCloseTime,
        timer: checkGlobalTimer(),
      });

      updateShowVlyby();
    }
  };
  const updateShowVlyby = () => {
    const localCheck =
      enableVlyby &&
      getIsPageVisible() &&
      !session?.user?.premium &&
      !isAdblockDetected;
    // open &&

    setShowVlyby(localCheck);
    setVideoAdActive(localCheck);

    if (localCheck) {
      clearTimeout(hideAdTimeoutId.current);
      hideAdTimeoutId.current = setTimeout(() => {
        if (typeof window !== "undefined") {
          console.log(
            "[[VLYBY]] timeout",
            hideTimeout,
            isVlybyAdReady,
            (window as any).isVlybyAdReady,
            showVlyby,
            localCheck
          );
          if (!(window as any).isVlybyAdReady) {
            stopVlybyVideoAds(false, false, true);
          }
        }
      }, hideTimeout);
    } else {
      stopVlybyVideoAds();
    }
  };

  const hideTimeout = useMemo(() => {
    if (networkState.effectiveType) {
      if (["2g", "slow-2g"].includes(networkState.effectiveType)) {
        return 30000;
      }
      if (["3g"].includes(networkState.effectiveType)) {
        return 15000;
      }
    }
    return 10000;
  }, [networkState]);

  // start loading vlyby
  useEffect(() => {
    clearTimeout(hideAdTimeoutId.current);
    clearTimeout(showAdTimeoutId.current);

    if (enableVlyby && open && isFirstVlybyAdPlay) {
      // first ad after 30s
      showAdTimeoutId.current = setTimeout(
        () => {
          console.log("FIRST LOAD");

          setIsFirstVlybyAdPlay(false);
          startLoadingVlyby();
        },
        checkGlobalTimer()
          ? firstAudioAdTimeout
          : VLYBY_TIMEOUT - (Number(Date.now()) - globalAdCloseTime) + 10
      );
    } else if (enableVlyby && !open) {
      (window as any).isVlybyAdReady = false;
      setIsVlybyAdReady(false);
      setVideoAdActive(false);
      setShowVlyby(false);
      setIsFirstVlybyAdPlay(true);
    }
  }, [open]);

  const getAdContainerMaximumSize = (): {
    desiredHeight: number;
    desiredWidth: number;
  } => {
    let maxWidth = 960;
    if (window.innerWidth < 1920) {
      maxWidth = 880;
    }
    if (window.innerWidth < 1600) {
      maxWidth = 600;
    }
    if (window.innerWidth < 1380) {
      maxWidth = 500;
    }
    const maximumHeight =
      window.innerHeight -
      252 -
      (controlsContainerRef.current?.clientHeight || 0);
    const proportionalMaximumWidth = maximumHeight * (16 / 9);
    const maximumWidth =
      proportionalMaximumWidth > maxWidth ? maxWidth : proportionalMaximumWidth;
    const desiredWidth =
      window.innerWidth - 80 > maximumWidth
        ? maximumWidth
        : window.innerWidth - 80;
    const desiredHeight = desiredWidth * (9 / 16);
    return { desiredHeight, desiredWidth };
  };

  const [adPlayerWidth, setAdPlayerWidth] = useState("");
  const [adPlayerHeight, setAdPlayerHeight] = useState("");

  useEffect(() => {
    if (enableVlyby) {
      const { desiredHeight, desiredWidth } = getAdContainerMaximumSize();
      setAdPlayerWidth(`${desiredWidth}px`);
      setAdPlayerHeight(`${desiredHeight}px`);
    }
  }, [width, height]);

  return (
    <CSSTransition
      nodeRef={wrapperRef}
      in={open}
      timeout={400}
      classNames={{
        enterActive: styles.player_fullscreen_enter_active,
        enter: styles.player_fullscreen_enter,
        enterDone: styles.player_fullscreen_enter_done,
        exit: styles.player_fullscreen_exit,
        exitActive: styles.player_fullscreen_exit_active,
        exitDone: styles.player_fullscreen_exit_done,
      }}
    >
      <div className={styles.player_fullscreen} ref={wrapperRef}>
        <div className={styles.player_fullscreen_wrapper}>
          <div
            className={styles.player_fullscreen_content}
            style={playerCssVariables}
          >
            <div className={styles.player_fullscreen_inner}>
              <div
                className={styles.player_fullscreen_header}
                data-navigate-row={open}
              >
                <button
                  onClick={() => onClose()}
                  data-navigate-item={open}
                  className={styles.player_fullscreen_close}
                >
                  <MemoAngleDown />
                </button>
              </div>
              <div className={clsx(styles.player_fullscreen_track)}>
                <div
                  className={clsx(
                    styles.player_fullscreen_track,
                    styles.player_fullscreen_ad_wrapper,
                    {
                      [styles.video_ad]: showVlyby,
                      [styles.invisible]: !videoAdActive && !showVlyby,
                      [styles.video_ad_ready]: (window as any).isVlybyAdReady,
                    }
                  )}
                >
                  {(window as any).isVlybyAdReady && (
                    <>
                      <div className={styles.video_ad_header}>
                        <Link href={"/get-premium"} passHref>
                          <a data-navigate-row={videoAdActive || showVlyby}>
                            <Button
                              accent
                              data-navigate-item={videoAdActive || showVlyby}
                              data-navigate-noscroll
                              className={styles.premium_button}
                              onClick={() => onClose()}
                            >
                              {t("premium.go-premium-for", {
                                var1: `${priceData.amount}${priceData.currency_symbol}`,
                              })}
                            </Button>
                          </a>
                        </Link>
                      </div>
                      <p className={styles.ad_header}>
                        {t("premium.video-ad-description")}
                      </p>
                    </>
                  )}
                  <div
                    className={styles.ad_player}
                    style={{
                      display: !showVlyby ? "none" : undefined,
                      margin: "0px auto",
                      maxWidth: "853px",
                    }}
                  >
                    {showVlyby && (
                      <div className={styles.vlyby_container}>
                        <VlybyContainer
                          width={adPlayerWidth}
                          height={adPlayerHeight}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <img
                  src={podcastChapter.logo ?? podcast.logo}
                  alt=""
                  className={clsx(styles.podcast_logo, {
                    [styles.invisible]: (window as any).isVlybyAdReady,
                  })}
                />
                <div
                  className={clsx(styles.player_fullscreen_track_info, {
                    [styles.invisible]: (window as any).isVlybyAdReady,
                  })}
                >
                  <div
                    className={clsx(styles.podcast_details, {
                      [styles.invisible]: (window as any).isVlybyAdReady,
                    })}
                  >
                    <h2 className={styles.title}>{podcast.name}</h2>
                  </div>
                  <div
                    className={clsx(styles.chapter_details, {
                      [styles.invisible]: (window as any).isVlybyAdReady,
                    })}
                  >
                    <h3>{podcastChapter.name}</h3>
                    {podcastChapter.summary ? (
                      <p className={styles.subtitle}>
                        {podcastChapter.summary}
                      </p>
                    ) : (
                      podcast.description && (
                        <p className={styles.subtitle}>{podcast.description}</p>
                      )
                    )}
                  </div>
                </div>
              </div>
              <div
                className={clsx(styles.player_fullscreen_controls, {
                  [styles.video_ad_ready]: (window as any).isVlybyAdReady,
                })}
                data-navigate-row={open}
              >
                <button
                  className={clsx(
                    styles.button_secondary,
                    styles.controls_first
                  )}
                  data-navigate-item={open}
                  onClick={() => goToPodcastDetails()}
                >
                  <MemoInfo />
                </button>
                <button
                  className={clsx(styles.button_priamry, styles.heart)}
                  onClick={() => toggleFavorite(podcast, playbackState.playing)}
                  data-navigate-item={open}
                  data-navigate-noscroll
                >
                  {loading ? (
                    <Loader />
                  ) : isFavoriteCategory(podcast) ? (
                    <MemoHeartFull />
                  ) : (
                    <MemoHeartOutline />
                  )}
                </button>
                <button
                  className={clsx(styles.play, {
                    [styles.show_play]: !playbackState.playing,
                  })}
                  data-navigate-item={open}
                  data-navigate-noscroll
                  onClick={(e) => {
                    e.stopPropagation();
                    playbackControls.toggle();
                  }}
                >
                  {playbackState.loading ? (
                    <MemoLoader />
                  ) : playbackState.playing ? (
                    <MemoPause />
                  ) : (
                    <MemoPlay />
                  )}
                </button>

                <button
                  className={clsx(styles.button_priamry)}
                  id="speed-control-button"
                  onClick={() => setSpeedSidebarOpen(true)}
                  data-navigate-item={open}
                  data-navigate-noscroll
                >
                  <MemoSpeedControl />
                </button>
                <button
                  className={clsx(
                    styles.button_secondary,
                    styles.controls_last
                  )}
                  onClick={() => setSidebarOpen(true)}
                  data-navigate-item={open}
                  data-navigate-noscroll
                >
                  <MemoPlaylist />
                </button>
              </div>

              <div
                data-navigate-row={open}
                className={clsx(
                  styles.player_fullscreen_progress_wrapper,
                  styles.player_fullscreen_controls,
                  {
                    [styles.video_ad_ready_progress]: (window as any)
                      .isVlybyAdReady,
                  }
                )}
              >
                {playbackState.duration !== null &&
                  playbackState.duration > 0 && (
                    <>
                      <button
                        data-navigate-item={open}
                        data-navigate-noscroll
                        className={clsx(
                          styles.button_secondary,
                          styles.button_jump
                        )}
                        onClick={() => jumpBack()}
                      >
                        <MemoWindBack withText />
                      </button>

                      <div className={styles.player_fullscreen_track_wrapper}>
                        <ProgressSlider
                          range={playerRange}
                          start={0}
                          progress={currentTime}
                          onChange={handleChange}
                          onStart={() => setStreamDragging(true)}
                          disabled={!!playbackState.activeAdStream}
                        />

                        <div className={styles.player_fullscreen_time}>
                          {formatTime(currentTime)}
                        </div>
                        <div
                          className={clsx(
                            styles.player_fullscreen_time,
                            styles.player_fullscreen_time_end
                          )}
                        >
                          {formatTime(playbackState.duration)}
                        </div>
                      </div>

                      <button
                        data-navigate-item={open}
                        data-navigate-noscroll
                        className={clsx(
                          styles.button_secondary,
                          styles.button_jump
                        )}
                        onClick={() => jumpForward()}
                      >
                        <MemoWindForward withText />
                      </button>
                    </>
                  )}
              </div>
            </div>
          </div>
          <PodcastSidebar
            open={sidebarOpen}
            podcast={podcast}
            onClose={sidebarCloseCallback}
          />
          <PodcastSpeedSidebar
            open={speedSidebarOpen}
            onClose={speedSidebarCloseCallback}
            currentSpeed={playbackState.playbackRate}
            onSpeedUp={playbackSpeedUp}
            onSpeedDown={playbackSpeedDown}
            podcastChapter={podcastChapter}
          />
        </div>
      </div>
    </CSSTransition>
  );
};

interface PodcastSidebarProps {
  open: boolean;
  podcast: Category;
  onClose: VoidFunction;
}
const PodcastSidebar: React.FC<PodcastSidebarProps> = ({ open, onClose }) => {
  const sidebarRef = useArrowKeyNavigationOverlay(open);
  const { podcastQueue } = useAppSelector(selectPlayerState);
  const dispatch = useAppDispatch();

  const formatPublishDate = (dateString: string) => {
    try {
      const parsedDate = parseISO(dateString);
      return format(parsedDate, "d MMM yyyy", {
        locale: getDateFnsLocale(),
      });
    } catch (e) {
      console.error(e);
      return "";
    }
  };

  const t = useTranslate();

  useClickAway(sidebarRef, (e) => {
    onClose();
  });
  const goBackCallback = useCallback(() => {
    onClose();
  }, [onClose]);
  useGoBackEvent("PodcastSidebar", goBackCallback, open);
  return (
    <CSSTransition
      nodeRef={sidebarRef}
      in={open}
      timeout={400}
      classNames={{
        enterActive: styles.player_sidebar_enter_active,
        enter: styles.player_sidebar_enter,
        enterDone: styles.player_sidebar_enter_done,
        exit: styles.player_sidebar_exit,
        exitActive: styles.player_sidebar_exit_active,
        exitDone: styles.player_sidebar_exit_done,
      }}
    >
      <div className={styles.player_sidebar} ref={sidebarRef}>
        <div className={styles.player_sidebar_inner}>
          {podcastQueue.items.map((item, itemIndex) => (
            <div
              key={`sidebar-item-${item.permalink}`}
              data-navigate-row={open}
            >
              <div
                className={styles.queue_item}
                data-navigate-item={open}
                tabIndex={0}
                onClick={() => {
                  dispatch(
                    playerActions.setPodcastQueue({
                      items: podcastQueue.items,
                      activeIndex: itemIndex,
                    })
                  );
                }}
              >
                <p className={styles.queue_item_name}>{item.name}</p>
                <p className={styles.queue_item_duration}>
                  {formatPublishDate(item?.published || "")} -{" "}
                  {formatTimeWithUnits(item.duration, t)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </CSSTransition>
  );
};

interface PodcastSpeedSidebarProps {
  open: boolean;
  onClose: VoidFunction;
  onSpeedUp: VoidFunction;
  onSpeedDown: VoidFunction;
  currentSpeed: number;
  podcastChapter: PodcastChapter;
}
const PodcastSpeedSidebar: React.FC<PodcastSpeedSidebarProps> = ({
  open,
  onClose,
  onSpeedUp,
  onSpeedDown,
  currentSpeed,
}) => {
  const sidebarRef = useArrowKeyNavigationOverlay(open);
  const { arrowNavigationEnabled } = useFeaturesContext();
  const goBackCallback = useCallback(() => {
    onClose();
  }, [onClose]);
  useGoBackEvent("PodcastSpeedSidebar", goBackCallback, open);

  useClickAway(sidebarRef, (e: MouseEvent) => {
    if (
      open &&
      !e
        ?.composedPath()
        ?.filter((item) => (item as HTMLElement).id === "speed-control-button")
        .length
    ) {
      onClose();
    }
  });

  return (
    <CSSTransition
      nodeRef={sidebarRef}
      in={open}
      timeout={400}
      classNames={{
        enterActive: styles.player_sidebar_enter_active,
        enter: styles.player_sidebar_enter,
        enterDone: styles.player_sidebar_enter_done,
        exit: styles.player_sidebar_exit,
        exitActive: styles.player_sidebar_exit_active,
        exitDone: styles.player_sidebar_exit_done,
      }}
    >
      <div
        className={clsx(styles.player_sidebar, styles.player_sidebar_speed)}
        ref={sidebarRef}
      >
        <div className={styles.player_sidebar_inner}>
          <div data-navigate-row={open} className={styles.speed_control}>
            <button
              data-navigate-item={open}
              data-navigate-noscroll
              onClick={() => onSpeedUp()}
            >
              <MemoBigPlus />
            </button>
          </div>
          <div className={styles.current_speed}>{currentSpeed}x</div>
          <div data-navigate-row={open} className={styles.speed_control}>
            <button
              data-navigate-item={open}
              data-navigate-noscroll
              onClick={() => onSpeedDown()}
            >
              <MemoBigMinus />
            </button>
          </div>
          {!arrowNavigationEnabled && (
            <div className={styles.close_container}>
              <button
                data-navigate-item={open}
                className={styles.close}
                data-navigate-noscroll
                onClick={() => onClose()}
              >
                <MemoArrowBack />
              </button>
            </div>
          )}
        </div>
      </div>
    </CSSTransition>
  );
};
